import { request } from './api'
import type { AdminInvite, AdminInviteInput, DietaryInput, RoomType } from './types'

/**
 * The admin console's API.
 *
 * Kept apart from api.ts because it is a different credential: the console is
 * protected by a single password held by the couple, not by the per-invite
 * codes guests use, and it rides a different cookie. Holding one grants nothing
 * of the other.
 *
 * Every route here answers 404 rather than 401 when the server has no admin
 * password configured, so a deployment without one does not advertise that a
 * console exists.
 */

interface AuthenticatedResponse {
  authenticated: boolean
}

/** Whether the browser already holds a valid admin session. */
export async function getSession(): Promise<boolean> {
  const body = await request<AuthenticatedResponse>('/api/admin/session')
  return body.authenticated
}

/** Exchanges the admin password for a session cookie. */
export async function login(password: string): Promise<void> {
  await request<AuthenticatedResponse>('/api/admin/session', {
    method: 'POST',
    body: JSON.stringify({ password }),
  })
}

export function logout(): Promise<void> {
  return request<void>('/api/admin/session', { method: 'DELETE' })
}

/** Every invite, with everyone on it and their responses. */
export async function listInvites(): Promise<AdminInvite[]> {
  const body = await request<{ invites: AdminInvite[] }>('/api/admin/invites')
  return body.invites
}

/** Creates an invite. An empty code asks the server to generate one. */
export function createInvite(input: AdminInviteInput): Promise<AdminInvite> {
  return request<AdminInvite>('/api/admin/invites', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

/** Replaces an invite's code and the exact set of people on it. */
export function saveInvite(id: string, input: AdminInviteInput): Promise<AdminInvite> {
  return request<AdminInvite>(`/api/admin/invites/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  })
}

export function deleteInvite(id: string): Promise<void> {
  return request<void>(`/api/admin/invites/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

/**
 * Records a guest's dietary requirements on their behalf, for the replies that
 * arrive by text.
 */
export function setDietary(personId: string, input: DietaryInput): Promise<void> {
  return request<void>(`/api/admin/people/${encodeURIComponent(personId)}/dietary`, {
    method: 'PUT',
    body: JSON.stringify(input),
  })
}

/** The rooms in Horsley Towers, with how many of each are taken. */
export async function listRooms(): Promise<RoomType[]> {
  const body = await request<{ types: RoomType[] }>('/api/admin/rooms')
  return body.types
}

/**
 * Puts an invite in a room, or moves it if it already has one.
 *
 * Held to the same rules a guest is: the server refuses a full room and refuses
 * an invite where nobody is coming, so the console cannot over-allocate.
 */
export function reserveRoom(inviteId: string, roomTypeId: string): Promise<AdminInvite> {
  return request<AdminInvite>(`/api/admin/invites/${encodeURIComponent(inviteId)}/room`, {
    method: 'PUT',
    body: JSON.stringify({ room_type_id: roomTypeId }),
  })
}

/** Takes an invite's room back. */
export function releaseRoom(inviteId: string): Promise<AdminInvite> {
  return request<AdminInvite>(`/api/admin/invites/${encodeURIComponent(inviteId)}/room`, {
    method: 'DELETE',
  })
}

/**
 * Records that an invite's room has been settled up, or takes the mark off.
 *
 * Console only, and there is no guest equivalent: the money changes hands at the
 * venue on the way out or with the couple, so this is a record of what happened
 * rather than a payment. Marking an already-paid room paid leaves the original
 * date alone.
 */
export function setRoomPaid(inviteId: string, paid: boolean): Promise<AdminInvite> {
  return request<AdminInvite>(`/api/admin/invites/${encodeURIComponent(inviteId)}/room/paid`, {
    method: 'PUT',
    body: JSON.stringify({ paid }),
  })
}
